<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { Dropdown, Modal } from "@/components";
import {
  useAllDocumentSpaceConfigsQuery,
  useCopyDocumentSpaceConfigMutation,
  useCreateDocumentSpaceConfigWithDefaultsMutation,
  useDocumentSpaceCompatibilityGroupsQuery,
} from "@/graphql/operations";
import { useRouter } from "vue-router";
import { useFlashMessage } from "@/hooks/useFlashMessage";
import { useI18n } from "vue-i18n";

type Mode = "new" | "copy";

const emit = defineEmits(["close"]);

const { t } = useI18n();
const router = useRouter();
const { displayFlashMessage } = useFlashMessage();

const createNameLabel = computed(() => t("templates.createModal.createName"));
const name = ref(createNameLabel.value);
const lastAutoName = ref(createNameLabel.value);
const mode = ref<Mode>("new");
const teamId = ref("");
const sourceConfigId = ref("");
const compatibilityGroupId = ref("");
const compatibilityGroupTouched = ref(false);
const isSubmitting = ref(false);

const { data: allConfigsData } = useAllDocumentSpaceConfigsQuery();
const teamNodes = computed(() =>
  (allConfigsData.value?.myAdministratedTeams?.nodes ?? []).filter(Boolean),
);
const teams = computed(() =>
  teamNodes.value.map((team) => ({
    value: team!.id.toString(),
    title: team!.name,
  })),
);

const tenantIdByTeamId = computed(() => {
  const map = new Map<string, string>();
  for (const team of teamNodes.value) {
    if (team!.tenant?.id) {
      map.set(team!.id.toString(), team!.tenant.id.toString());
    }
  }
  return map;
});

const selectedTenantId = computed(
  () => tenantIdByTeamId.value.get(teamId.value) ?? null,
);

watch(
  teams,
  (items) => {
    if (items.length === 0) {
      teamId.value = "";
      return;
    }

    if (!items.some((item) => item.value === teamId.value)) {
      teamId.value = items[0].value;
    }
  },
  { immediate: true },
);

const sourceTemplates = computed(() => {
  const adminTeams = allConfigsData.value?.myAdministratedTeams?.nodes ?? [];

  return adminTeams
    .filter(Boolean)
    .flatMap((team) =>
      (team?.documentSpaceConfigs?.nodes ?? [])
        .filter(Boolean)
        .map((config) => ({
          value: config!.id.toString(),
          title: `${config!.name || t("templates.unnamed")} · ${team!.name}`,
          templateName: config!.name || t("templates.unnamed"),
          groupId: config!.groupId?.toString() ?? null,
          tenantId: team!.tenant?.id?.toString() ?? null,
        })),
    )
    .sort((left, right) => left.title.localeCompare(right.title));
});

const hasCopySources = computed(() => sourceTemplates.value.length > 0);

watch(
  sourceTemplates,
  (items) => {
    if (items.length === 0) {
      sourceConfigId.value = "";
      mode.value = "new";
      return;
    }

    if (!items.some((item) => item.value === sourceConfigId.value)) {
      sourceConfigId.value = items[0].value;
    }
  },
  { immediate: true },
);

const selectedSourceTemplate = computed(
  () =>
    sourceTemplates.value.find((item) => item.value === sourceConfigId.value) ??
    null,
);

const { data: compatibilityGroupsData } =
  useDocumentSpaceCompatibilityGroupsQuery();
const allCompatibilityGroups = computed(
  () =>
    compatibilityGroupsData.value?.documentSpaceLinkGroups?.nodes
      ?.filter(Boolean)
      .map((group) => ({
        value: group!.id.toString(),
        title: group!.name,
        tenantId: group!.tenantId?.toString(),
      })) ?? [],
);

const compatibilityGroups = computed(() => {
  const tenantId = selectedTenantId.value;
  if (!tenantId) {
    return [];
  }

  return allCompatibilityGroups.value
    .filter((group) => group.tenantId === tenantId)
    .map((group) => ({ value: group.value, title: group.title }))
    .sort((left, right) => {
      if (BigInt(left.value) === BigInt(right.value)) {
        return 0;
      }

      return BigInt(left.value) < BigInt(right.value) ? -1 : 1;
    });
});

const automaticCompatibilityGroupId = computed(() => {
  if (mode.value === "copy") {
    const source = selectedSourceTemplate.value;

    if (
      source?.tenantId &&
      source.tenantId === selectedTenantId.value &&
      source.groupId &&
      compatibilityGroups.value.some((group) => group.value === source.groupId)
    ) {
      return source.groupId;
    }

    return "";
  }

  return compatibilityGroups.value[0]?.value ?? "";
});

watch([mode, teamId, sourceConfigId], () => {
  compatibilityGroupTouched.value = false;
});

watch(
  [automaticCompatibilityGroupId, compatibilityGroups],
  () => {
    const groups = compatibilityGroups.value;
    const currentSelectionIsValid =
      compatibilityGroupId.value === "" ||
      groups.some((group) => group.value === compatibilityGroupId.value);

    if (!currentSelectionIsValid) {
      compatibilityGroupId.value = "";
      compatibilityGroupTouched.value = false;
    }

    if (!compatibilityGroupTouched.value) {
      compatibilityGroupId.value = automaticCompatibilityGroupId.value;
    }
  },
  { immediate: true },
);

const setAutoName = (nextName: string) => {
  const trimmedName = name.value.trim();
  const mayOverwrite =
    trimmedName === "" ||
    name.value === lastAutoName.value ||
    name.value === createNameLabel.value;

  if (mayOverwrite) {
    name.value = nextName;
    lastAutoName.value = nextName;
  }
};

watch(
  [mode, selectedSourceTemplate, createNameLabel],
  () => {
    if (mode.value === "copy" && selectedSourceTemplate.value) {
      setAutoName(
        t("templateEditor.copyModal.copyName", {
          name: selectedSourceTemplate.value.templateName,
        }),
      );
      return;
    }

    setAutoName(createNameLabel.value);
  },
  { immediate: true },
);

watch(
  hasCopySources,
  (value) => {
    if (!value && mode.value === "copy") {
      mode.value = "new";
    }
  },
  { immediate: true },
);

const trimmedName = computed(() => name.value.trim());
const nameError = computed(() =>
  trimmedName.value.length >= 2 ? "" : t("templates.createModal.nameError"),
);

const canSubmit = computed(() => {
  if (trimmedName.value.length < 2 || !teamId.value || isSubmitting.value) {
    return false;
  }

  if (mode.value === "copy") {
    return !!sourceConfigId.value;
  }

  return true;
});

const setTeamId = (id: string) => {
  teamId.value = id;
};

const setSourceConfigId = (id: string) => {
  sourceConfigId.value = id;
};

const setCompatibilityGroupId = (id: string | null) => {
  compatibilityGroupId.value = id ?? "";
  compatibilityGroupTouched.value = true;
};

const { executeMutation: createTemplateWithDefaults } =
  useCreateDocumentSpaceConfigWithDefaultsMutation();
const { executeMutation: copyTemplate } = useCopyDocumentSpaceConfigMutation();

const submit = async () => {
  if (!canSubmit.value) {
    return;
  }

  isSubmitting.value = true;

  if (mode.value === "new") {
    const res = await createTemplateWithDefaults({
      name: trimmedName.value,
      teamId: teamId.value,
      compatibilityGroupId: compatibilityGroupId.value || null,
    });

    const newConfigId =
      res.data?.createDocumentSpaceConfigWithDefaults?.newConfig?.id;

    if (!res.error && newConfigId) {
      displayFlashMessage(t("templates.createModal.successCreate"), "success");
      emit("close");
      await router.push({
        name: "TemplateEditor",
        params: { id: newConfigId },
      });
      isSubmitting.value = false;
      return;
    }
  } else {
    const res = await copyTemplate({
      id: sourceConfigId.value,
      teamId: teamId.value,
      name: trimmedName.value,
      copyTranslations: true,
      compatibilityGroupId: compatibilityGroupId.value || null,
    });

    const newConfigId = res.data?.copyDocumentSpaceConfig?.newConfig?.id;

    if (!res.error && newConfigId) {
      displayFlashMessage(t("templates.createModal.successCopy"), "success");
      emit("close");
      await router.push({
        name: "TemplateEditor",
        params: { id: newConfigId },
      });
      isSubmitting.value = false;
      return;
    }
  }

  displayFlashMessage(t("error"), "error");
  isSubmitting.value = false;
};
</script>

<template>
  <Modal
    :active="true"
    :scrolling="true"
    :shrinkToContent="true"
    @close="emit('close')"
  >
    <template #header>
      {{ $t("templates.createModal.title") }}
    </template>

    <template #body>
      <div class="row">
        <label>{{ $t("templates.createModal.nameLabel") }}</label>
        <input
          v-model="name"
          class="input"
          type="text"
          :class="{ invalid: !!nameError }"
        />
        <p v-if="nameError" class="help danger">{{ nameError }}</p>
      </div>

      <div class="row" v-if="teams.length > 1">
        <label>{{ $t("templates.createModal.teamSelection.label") }}</label>
        <Dropdown
          :items="teams"
          :value="teamId"
          :displayTitle="true"
          @change="setTeamId"
        />
      </div>

      <div class="row">
        <label>{{ $t("templates.createModal.modeLabel") }}</label>
        <div class="mode_picker">
          <label
            class="mode_option"
            :class="{ disabled: false, selected: mode === 'new' }"
          >
            <input v-model="mode" type="radio" value="new" />
            <span class="mode_title">{{
              $t("templates.createModal.mode.new.label")
            }}</span>
            <span class="mode_description">
              {{ $t("templates.createModal.mode.new.description") }}
            </span>
          </label>

          <label
            class="mode_option"
            :class="{ disabled: !hasCopySources, selected: mode === 'copy' }"
          >
            <input
              v-model="mode"
              type="radio"
              value="copy"
              :disabled="!hasCopySources"
            />
            <span class="mode_title">{{
              $t("templates.createModal.mode.copy.label")
            }}</span>
            <span class="mode_description">
              {{ $t("templates.createModal.mode.copy.description") }}
            </span>
          </label>
        </div>
      </div>

      <div class="row" v-if="mode === 'copy' && hasCopySources">
        <label>{{ $t("templates.createModal.sourceSelection.label") }}</label>
        <Dropdown
          :items="sourceTemplates"
          :value="sourceConfigId"
          :displayTitle="true"
          @change="setSourceConfigId"
        />
      </div>

      <p v-if="mode === 'copy' && !hasCopySources" class="help">
        {{ $t("templates.createModal.sourceSelection.empty") }}
      </p>

      <div class="row" v-if="compatibilityGroups.length > 1">
        <label>{{
          $t("templates.createModal.compatibilityGroupSelection.label")
        }}</label>
        <Dropdown
          :items="compatibilityGroups"
          :value="compatibilityGroupId"
          :displayTitle="true"
          :placeholder="
            $t('templates.createModal.compatibilityGroupSelection.placeholder')
          "
          @change="setCompatibilityGroupId"
        />
      </div>
    </template>

    <template #footer>
      <div class="buttons">
        <button class="button" @click="emit('close')">
          {{ $t("cancel") }}
        </button>
        <button
          class="button primary"
          :class="{ disabled: !canSubmit }"
          @click="submit"
        >
          {{
            $t(
              mode === "new"
                ? "templates.createModal.submitCreate"
                : "templates.createModal.submitCopy",
            )
          }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.row {
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  margin-bottom: 1.5em;
}

label {
  font-size: 0.9em;
}

.help {
  font-size: 0.85em;
  margin: 0;
}

.help.danger {
  color: var(--classic-danger, #cf2c2c);
}

.mode_picker {
  display: grid;
  gap: 0.75em;
}

.mode_option {
  border: 1px solid var(--classic-border, #d7d7d7);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 0.25em;
  padding: 0.85em 1em;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;
}

.mode_option.selected {
  border-color: var(--classic-accent, #2a7de1);
  background: var(--classic-accent-soft, #f3f8ff);
}

.mode_option.disabled {
  opacity: 0.55;
}

.mode_option input {
  margin: 0 0 0.25em 0;
}

.mode_title {
  font-weight: 600;
}

.mode_description {
  color: var(--classic-text-secondary, #666);
  font-size: 0.9em;
}
</style>
